import React from "react";

import pdf from "@react-pdf/renderer";

import Macronutrients from "@/components/MealPlanForm/store/Macronutrients";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { fraction, round } from "@/utils/number";
import { humanize } from "@/utils/string";
import { FragmentType } from "@/utils/type";

import Divider from "./Divider";
import Flex from "./layout/Flex";

const { Text, View, StyleSheet } = pdf;

const styles = StyleSheet.create({
  root: {
    width: "100%",
    margin: "12px 0px",
    fontWeight: "thin",
    fontSize: "10px",
    padding: "12px 0px",
    height: "auto",
  },
  title: {
    fontWeight: "ultrabold",
    fontSize: "12px",
  },
  bold: {
    fontWeight: "bold",
  },
  textSecondary: {
    color: colors.grey,
    fontWeight: "semibold",
  },
  macros: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  macro: {
    marginTop: "4px",
  },
  rowItem: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    fontWeight: "semibold",
    fontSize: "10px",
    marginBottom: "12px",
  },
  grayBackground: {
    backgroundColor: colors.light,
  },
});

type Props = {
  data: FragmentType<typeof sectionFragment>;
  macronutrients?: Maybe<FragmentType<typeof sectionMacronutrientsFragment>>;
  day: number;
};

function isEven(value: number): boolean {
  if (value % 2 == 0) return true;
  else return false;
}

const sectionFragment = graphql(`
  fragment PDFMealPlanSection on PlannedMeal {
    id
    meal
    recommendedTime
    itemsByDate {
      date
      totalEnergyKcal
      items {
        id
        amount
        energyKcal
        food {
          id
          name
        }
        foodPortion {
          id
          unitWithModifier
        }
      }
    }
  }
`);

const sectionMacronutrientsFragment = graphql(`
  fragment PDFMealPlanSection_Macronutrients on MealPlan {
    id
    macronutrientsCarbohydratePercent
    macronutrientsProteinPercent
    macronutrientsFatPercent
  }
`);

const Section: React.FC<Props> = ({ data, macronutrients: macro, day }) => {
  const itemsForDate = data.itemsByDate[day - 1];

  const carbohydrateGrams = Macronutrients.macronutrientGrams(
    "carbohydrates",
    fraction(macro?.macronutrientsCarbohydratePercent ?? 0),
    itemsForDate.totalEnergyKcal
  );
  const proteinGrams = Macronutrients.macronutrientGrams(
    "proteins",
    fraction(macro?.macronutrientsProteinPercent ?? 0),
    itemsForDate.totalEnergyKcal
  );
  const fatGrams = Macronutrients.macronutrientGrams(
    "fats",
    fraction(macro?.macronutrientsFatPercent ?? 0),
    itemsForDate.totalEnergyKcal
  );

  return (
    <View wrap={false}>
      <View style={styles.root}>
        <Flex
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="start"
        >
          <View style={{ width: "30%", height: "100%" }}>
            <Flex
              alignItems="start"
              flexDirection="column"
              justifyContent="spaceBetween"
            >
              <View>
                <Text style={styles.title}>{humanize(data.meal)}</Text>
                <Text style={styles.bold}>
                  {format({ timeofday: data.recommendedTime }, "hh:mm a")}
                </Text>
                <View style={{ marginBottom: "4px" }} />
                <Text style={[styles.bold, styles.textSecondary]}>
                  {`${Math.round(itemsForDate.totalEnergyKcal)} kcal`}
                </Text>
              </View>

              <View style={{ marginBottom: "8px" }} />

              {Features.pdfMacronutrientsEnabled && (
                <View style={styles.macros}>
                  <View>
                    <Flex flexDirection="row" alignItems="center">
                      <Text style={styles.bold}>Carbohydrates:</Text>
                      <View style={{ marginRight: "2px" }} />
                      <Text style={styles.textSecondary}>
                        {`${macro?.macronutrientsCarbohydratePercent ?? 0}%`}
                        {`(${round(carbohydrateGrams, 2)}g)`}
                      </Text>
                    </Flex>
                  </View>

                  <View style={styles.macro}>
                    <Flex flexDirection="row" alignItems="center">
                      <Text style={styles.bold}>Protein:</Text>
                      <View style={{ marginRight: "2px" }} />
                      <Text style={styles.textSecondary}>
                        {`${macro?.macronutrientsProteinPercent ?? 0}%`}
                        {`(${round(proteinGrams, 2)}g)`}
                      </Text>
                    </Flex>
                  </View>

                  <View style={styles.macro}>
                    <Flex flexDirection="row" alignItems="center">
                      <Text style={styles.bold}>Fat:</Text>
                      <View style={{ marginRight: "2px" }} />
                      <Text style={styles.textSecondary}>
                        {`${macro?.macronutrientsFatPercent ?? 0}%`}
                        {`(${round(fatGrams, 2)}g)`}
                      </Text>
                    </Flex>
                  </View>
                </View>
              )}
            </Flex>
          </View>

          <View style={{ width: "65%" }}>
            {itemsForDate.items.length !== 0 ? (
              <>
                <View>
                  {itemsForDate.items.map((item, index) => {
                    const grayBackground = isEven(index + 1)
                      ? styles.grayBackground
                      : styles.rowItem;

                    return (
                      <View
                        style={[styles.rowItem, grayBackground]}
                        key={index}
                      >
                        <View style={{ width: "40%" }}>
                          <Text>{item.food.name}</Text>
                        </View>
                        <View style={{ marginRight: "12px" }} />
                        <View style={{ width: "60%" }}>
                          <View style={{ width: "100%" }}>
                            <Flex
                              justifyContent="spaceBetween"
                              flexDirection="row"
                            >
                              <View
                                style={{
                                  width: "50%",
                                  alignSelf: "flex-start",
                                }}
                              >
                                <Text
                                  style={{ alignSelf: "flex-start" }}
                                >{`${item.amount} ${item.foodPortion.unitWithModifier}`}</Text>
                              </View>
                              <View style={{ marginRight: "12px" }} />
                              <View style={{ width: "50%" }}>
                                <Text
                                  style={{ alignSelf: "flex-end" }}
                                >{`${Math.round(item.energyKcal)} kcal`}</Text>
                              </View>
                            </Flex>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </>
            ) : (
              <Flex alignItems="center" justifyContent="center">
                <Text>No data</Text>
              </Flex>
            )}
          </View>
        </Flex>
      </View>
      <Divider size="large" />
    </View>
  );
};

export default Section;
